import React, { useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import { exportappointmentsbydaterangeAPI } from "../../src/helpers/api.ts";
import Swal from "sweetalert2";

const SpaAppointmentExcelExport = ({ hotelId, onExportComplete }) => {
  const [showModal, setShowModal] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [loading, setLoading] = useState(false);

  const handleShowModal = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    setDateRange({
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDateRange({ startDate: "", endDate: "" });
  };

  // ✅ FIX: Correct GST calculation helper
  const calcGst = (subtotal, discount, gstPct) => {
    const base = parseFloat(subtotal) || 0;
    const disc = parseFloat(discount) || 0;
    const pct  = parseFloat(gstPct)   || 0;
    const afterDiscount = base - disc;
    const gstAmt = Math.round((afterDiscount * pct) / 100 * 100) / 100;
    return { afterDiscount, gstAmt, total: afterDiscount + gstAmt };
  };

  const exportToExcel = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      Swal.fire({ icon: "warning", title: "तारीख निवडा", text: "कृपया दोन्ही तारखा निवडा" });
      return;
    }
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      Swal.fire({ icon: "warning", title: "चुकीची तारीख", text: "सुरुवातीची तारीख शेवटच्या तारखेपेक्षा मोठी असू शकत नाही" });
      return;
    }

    try {
      setLoading(true);
      const response = await exportappointmentsbydaterangeAPI({
        hotel_id: hotelId,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      });

      if (!response.success || !response.appointments) {
        throw new Error(response.message || "Appointments fetch failed");
      }

      const appointments = response.appointments;

      if (appointments.length === 0) {
        Swal.fire({
          icon: "info",
          title: "कोणतेही Appointments नाहीत",
          html: `<p>निवडलेल्या तारखांमध्ये कोणतेही appointments आढळले नाहीत.<br/><b>${dateRange.startDate}</b> ते <b>${dateRange.endDate}</b></p>`,
        });
        setLoading(false);
        return;
      }

      const workbook = XLSX.utils.book_new();

      // ─────────────────────────────────────────────
      // SHEET 1: Appointments Detail
      // ─────────────────────────────────────────────

      // Title rows
      const titleRows = [
        ["SPA APPOINTMENTS REPORT"],
        [`Date Range: ${dateRange.startDate} to ${dateRange.endDate}`],
        [`Generated: ${new Date().toLocaleString("en-IN")}`],
        [], // blank row
        // Headers
        [
          "Sr No",
          "Appointment ID",
          "Invoice No",
          "Date",
          "Time",
          "Customer Name",
          "Mobile Number",
          "Service Name",
          "Duration (min)",
          "Therapist",
          "Billing Type",
          "Status",
          "Base Price (₹)",
          "Discount (₹)",
          "Amount After Discount (₹)",
          `GST %`,
          "GST Amount (₹)",
          "CGST (₹)",
          "SGST (₹)",
          "Final Amount (₹)",
          "Billing Done",
        ],
      ];

      // ✅ Data rows with CORRECT calculations
      const dataRows = appointments.map((apt, idx) => {
        const { afterDiscount, gstAmt, total } = calcGst(
          apt.basePrice,
          apt.discount,
          apt.gstPercentage
        );
        const halfGst = Math.round((gstAmt / 2) * 100) / 100;

        return [
          idx + 1,
          apt.appointmentId,
          apt.invoiceNumber || "—",
          new Date(apt.appointmentTime).toLocaleDateString("en-GB"),
          new Date(apt.appointmentTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
          apt.customerName || "N/A",
          apt.customerMobile || "N/A",
          apt.serviceName || "N/A",
          apt.serviceDuration || "60",
          apt.therapistName || "N/A",
          apt.billingType === "room" ? "Room Charge" : "Walk-in",
          apt.status,
          parseFloat(apt.basePrice) || 0,
          parseFloat(apt.discount) || 0,
          afterDiscount,
          parseFloat(apt.gstPercentage) || 0,
          gstAmt,
          halfGst,
          halfGst,
          // ✅ Use final_amount from billing if available, else calculated
          apt.hasBilling ? (parseFloat(apt.totalPrice) || total) : total,
          apt.hasBilling ? "✅ Yes" : "❌ No",
        ];
      });

      // ✅ Summary totals row
      const totalBasePrice    = appointments.reduce((s, a) => s + (parseFloat(a.basePrice) || 0), 0);
      const totalDiscount     = appointments.reduce((s, a) => s + (parseFloat(a.discount) || 0), 0);
      const totalAfterDisc    = appointments.reduce((s, a) => s + calcGst(a.basePrice, a.discount, a.gstPercentage).afterDiscount, 0);
      const totalGstAmt       = appointments.reduce((s, a) => s + calcGst(a.basePrice, a.discount, a.gstPercentage).gstAmt, 0);
      const totalFinal        = appointments.reduce((s, a) => {
        const { total } = calcGst(a.basePrice, a.discount, a.gstPercentage);
        return s + (a.hasBilling ? (parseFloat(a.totalPrice) || total) : total);
      }, 0);

      const summaryRow = [
        "", "", "", "", "", "", "", "", "", "", "", "TOTAL →",
        Math.round(totalBasePrice * 100) / 100,
        Math.round(totalDiscount * 100) / 100,
        Math.round(totalAfterDisc * 100) / 100,
        "",
        Math.round(totalGstAmt * 100) / 100,
        Math.round((totalGstAmt / 2) * 100) / 100,
        Math.round((totalGstAmt / 2) * 100) / 100,
        Math.round(totalFinal * 100) / 100,
        "",
      ];

      const allRows = [...titleRows, ...dataRows, [], summaryRow];
      const ws1 = XLSX.utils.aoa_to_sheet(allRows);

      // Column widths
      ws1["!cols"] = [
        { wch: 6 },  // Sr No
        { wch: 14 }, // Appt ID
        { wch: 16 }, // Invoice No
        { wch: 12 }, // Date
        { wch: 8 },  // Time
        { wch: 22 }, // Customer Name
        { wch: 14 }, // Mobile
        { wch: 24 }, // Service
        { wch: 13 }, // Duration
        { wch: 18 }, // Therapist
        { wch: 13 }, // Billing Type
        { wch: 12 }, // Status
        { wch: 14 }, // Base Price
        { wch: 13 }, // Discount
        { wch: 22 }, // After Discount
        { wch: 8 },  // GST%
        { wch: 14 }, // GST Amt
        { wch: 12 }, // CGST
        { wch: 12 }, // SGST
        { wch: 16 }, // Final
        { wch: 12 }, // Billed
      ];

      XLSX.utils.book_append_sheet(workbook, ws1, "Spa Appointments");

      // ─────────────────────────────────────────────
      // SHEET 2: Summary
      // ─────────────────────────────────────────────
      const billedCount   = appointments.filter((a) => a.hasBilling).length;
      const pendingCount  = appointments.filter((a) => a.status === "PENDING").length;
      const completedCount= appointments.filter((a) => a.status === "COMPLETED").length;
      const cancelledCount= appointments.filter((a) => a.status === "CANCELLED").length;

      const summarySheet = [
        ["SPA APPOINTMENTS — SUMMARY"],
        [`Date Range: ${dateRange.startDate} to ${dateRange.endDate}`],
        [],
        ["METRIC", "VALUE"],
        ["Total Appointments", appointments.length],
        ["Completed", completedCount],
        ["Pending", pendingCount],
        ["Cancelled", cancelledCount],
        ["Billing Done", billedCount],
        ["Billing Pending", appointments.length - billedCount],
        [],
        ["FINANCIALS", "AMOUNT (₹)"],
        ["Total Base Price", Math.round(totalBasePrice * 100) / 100],
        ["Total Discount Given", Math.round(totalDiscount * 100) / 100],
        ["Amount After Discount", Math.round(totalAfterDisc * 100) / 100],
        ["Total GST Collected", Math.round(totalGstAmt * 100) / 100],
        ["  CGST", Math.round((totalGstAmt / 2) * 100) / 100],
        ["  SGST", Math.round((totalGstAmt / 2) * 100) / 100],
        ["TOTAL REVENUE (Final)", Math.round(totalFinal * 100) / 100],
      ];

      const ws2 = XLSX.utils.aoa_to_sheet(summarySheet);
      ws2["!cols"] = [{ wch: 28 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(workbook, ws2, "Summary");

      // Download
      const filename = `Spa_Appointments_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;
      XLSX.writeFile(workbook, filename);

      Swal.fire({
        icon: "success",
        title: "Export यशस्वी!",
        html: `
          <div class="text-start">
            <p><b>File:</b> ${filename}</p>
            <p><b>Total Records:</b> ${appointments.length}</p>
            <hr/>
            <p><b>Total Base Price:</b> ₹${Math.round(totalBasePrice)}</p>
            <p><b>Total Discount:</b> ₹${Math.round(totalDiscount)}</p>
            <p><b>Total GST:</b> ₹${Math.round(totalGstAmt)}</p>
            <p><b>Total Revenue:</b> ₹${Math.round(totalFinal)}</p>
          </div>`,
      });

      handleCloseModal();
      if (onExportComplete) onExportComplete();

    } catch (error) {
      console.error("Export error:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: error.message || "An unexpected error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="success" onClick={handleShowModal} className="mb-3">
        <FaFileExcel className="me-2" />
        Export to Excel
      </Button>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Spa Appointments — Excel Export</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Export करण्यासाठी तारखांची range निवडा. दोन sheets export होतील:<br />
            <b>Sheet 1:</b> सर्व Appointment details &nbsp;|&nbsp;
            <b>Sheet 2:</b> Financial Summary
          </p>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange((p) => ({ ...p, startDate: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange((p) => ({ ...p, endDate: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="success"
            onClick={exportToExcel}
            disabled={loading || !dateRange.startDate || !dateRange.endDate}
          >
            {loading ? "Exporting..." : "Export to Excel"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SpaAppointmentExcelExport;